import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Button, message } from 'antd';
import { AudioOutlined, MessageOutlined } from '@ant-design/icons';
import { convertAudioToText } from '../../../services/chatService';
import { uploadFile } from '../../../services/ossService';

const ButtonContainer = styled.div<{ isVoiceMode: boolean }>`
  display: flex;
  align-items: center;
  width: ${props => props.isVoiceMode ? '100%' : 'auto'};
`;

const ModeToggleButton = styled(Button)`
  flex-shrink: 0;
`;

const RecordButton = styled.button`
  flex: 1;
  height: 40px;
  border: 0;
  background: transparent;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #333;
  touch-action: none;
  cursor: pointer;
  outline: none;
  padding: 0 16px;
  transition: all 0.2s;
  user-select: none;
  -webkit-user-select: none;
  
  &:active, &.recording {
    color: #666;
  }

  &:hover {
    color: #666;
  }
`;

const RecordingAnimation = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.7);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: white;
  z-index: 1000;
`;

const WaveAnimation = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  margin: 10px 0;

  span {
    display: inline-block;
    width: 3px;
    height: 20px;
    background-color: #fff;
    animation: wave 1s infinite ease-in-out;
    margin: 0 2px;

    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.1s; }
    &:nth-child(3) { animation-delay: 0.2s; }
    &:nth-child(4) { animation-delay: 0.3s; }
    &:nth-child(5) { animation-delay: 0.4s; }
  }

  @keyframes wave {
    0%, 100% { transform: scaleY(0.5); }
    50% { transform: scaleY(1.5); }
  }
`;

const MAX_RECORDING_TIME = 60000; // 最大录音时长：60秒
const MIN_RECORDING_TIME = 1000;  // 最小录音时长：1秒
const MAX_INIT_RETRIES = 3; // 最大初始化重试次数

interface VoiceRecorderProps {
  onRecordingComplete: (blob: Blob) => void;
  onModeChange: (isVoiceMode: boolean) => void;
  isVoiceMode: boolean;
  threadId: string;
  accountId: string;
  onSendMessage: (text: string) => void;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onRecordingComplete,
  onModeChange,
  isVoiceMode,
  threadId,
  accountId,
  onSendMessage
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDeviceReady, setIsDeviceReady] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingTimer = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const [initRetryCount, setInitRetryCount] = useState(0);
  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 初始化录音设备
  const initializeRecorder = async () => {
    try {
      // 如果已经有流和录音器，直接返回成功
      if (streamRef.current && mediaRecorderRef.current) {
        setIsDeviceReady(true);
        return true;
      }

      // 清理旧的资源
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      mediaRecorderRef.current = null;

      // 请求新的音频流
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true  // 使用最简单的配置
      });

      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      setIsDeviceReady(true);
      return true;
    } catch (error) {
      console.error('Failed to initialize recorder:', error);
      setIsDeviceReady(false);
      message.error('无法访问麦克风，请确保已授予权限');
      return false;
    }
  };

  // 在进入语音模式时预初始化录音设备
  useEffect(() => {
    let mounted = true;

    if (isVoiceMode) {
      // 立即尝试初始化
      initializeRecorder().then((success) => {
        if (mounted && success) {
          setIsDeviceReady(true);
        }
      });
    } else {
      // 退出语音模式时释放资源
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current = null;
      }
      setIsDeviceReady(false);
    }

    return () => {
      mounted = false;
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
    };
  }, [isVoiceMode]);

  // 处理录音数据
  const handleRecordingData = async () => {
    try {
      const recordingDuration = Date.now() - startTimeRef.current;

      if (recordingDuration < MIN_RECORDING_TIME) {
        message.warning('录音时间太短');
        return;
      }

      if (!threadId || !accountId) {
        throw new Error('Missing threadId or accountId');
      }

      if (audioChunksRef.current.length === 0) {
        throw new Error('没有录到音频数据');
      }

      setIsProcessing(true);
      // message.loading('正在处理语音...', 0);

      // 合并音频数据
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm;codecs=opus' });

      // 检查音频大小
      if (audioBlob.size > 10 * 1024 * 1024) {
        throw new Error('音频文件过大');
      }

      // 1. 上传音频文件到服务器
      const audioFile = new File([audioBlob], `audio-${Date.now()}.webm`, { type: 'audio/webm;codecs=opus' });
      const audioUrl = await uploadFile(audioFile, 'audio', 'public-read');

      // 2. 调用音频转文字 API
      const text = await convertAudioToText(audioUrl);

      if (!text || text.trim() === '') {
        throw new Error('未能识别到有效语音内容');
      }

      // 3. 在聊天窗口显示文本消息
      onSendMessage(text);

    } catch (error) {
      console.error('Error processing audio:', error);
      message.error(error instanceof Error ? error.message : '语音处理失败');
    } finally {
      setIsProcessing(false);
      message.destroy();
      // 清理录音数据
      audioChunksRef.current = [];
    }
  };

  const startRecordingHandler = useCallback(async () => {
    if (!isVoiceMode || isProcessing) return;

    try {
      // 如果没有录音器，尝试初始化
      if (!mediaRecorderRef.current) {
        const initialized = await initializeRecorder();
        if (!initialized) return;
      }

      audioChunksRef.current = [];
      startTimeRef.current = Date.now();
      mediaRecorderRef.current?.start(10);
      setIsRecording(true);

      recordingTimer.current = setTimeout(() => {
        stopRecordingHandler();
        message.info('已达到最大录音时长');
      }, MAX_RECORDING_TIME);

    } catch (error) {
      console.error('Failed to start recording:', error);
      message.error('启动录音失败，请重试');
      setIsRecording(false);
    }
  }, [isVoiceMode, isProcessing]);

  const stopRecordingHandler = useCallback(() => {
    if (!isVoiceMode || !isRecording) return;

    if (recordingTimer.current) {
      clearTimeout(recordingTimer.current);
      recordingTimer.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      handleRecordingData();
    }

    setIsRecording(false);
  }, [isVoiceMode, isRecording]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    startRecordingHandler();
  }, [startRecordingHandler]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    stopRecordingHandler();
  }, [stopRecordingHandler]);

  const handleMouseDown = useCallback(() => {
    startRecordingHandler();
  }, [startRecordingHandler]);

  const handleMouseUp = useCallback(() => {
    stopRecordingHandler();
  }, [stopRecordingHandler]);

  const toggleMode = useCallback(() => {
    if (isRecording) {
      stopRecordingHandler();
    }
    onModeChange(!isVoiceMode);
  }, [isVoiceMode, onModeChange, isRecording, stopRecordingHandler]);

  // 组件卸载时清理资源
  React.useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (recordingTimer.current) {
        clearTimeout(recordingTimer.current);
      }
    };
  }, []);

  return (
    <ButtonContainer isVoiceMode={isVoiceMode}>
      <ModeToggleButton
        icon={isVoiceMode ? <MessageOutlined /> : <AudioOutlined />}
        onClick={toggleMode}
        type="text"
        disabled={isProcessing}
      />
      {isVoiceMode && (
        <RecordButton
          className={isRecording ? 'recording' : ''}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          disabled={isProcessing}
        >
          {isProcessing ? '处理中...' : isRecording ? '松开 发送' : !isDeviceReady ? '正在初始化...' : '按住 说话'}
        </RecordButton>
      )}
      {isRecording && (
        <RecordingAnimation>
          <WaveAnimation>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </WaveAnimation>
          <div>松开发送</div>
        </RecordingAnimation>
      )}
    </ButtonContainer>
  );
};

export default VoiceRecorder; 